import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Skeleton,
  Button,
  IconButton,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import {
  Folder,
  FolderOpen,
  ChevronRight,
  Search,
  Download,
  Upload,
  Trash2,
  Eye,
} from 'lucide-react';
import { DAM_DEPARTMENTS, type DamFile, type DamFolder } from 'mocks/dam';
import { listDamFolders, listDamFolderFiles, deleteCreatedFile } from 'services/demoApi/damApi';
import { downloadDamFile } from 'services/demoApi/fileDownload';
import DamPreviewDialog from 'components/DamPreviewDialog';
import {
  uploadDamFile,
  deleteUploadedFile,
  UploadValidationError,
  isIndexedDbAvailable,
} from 'services/demoApi/damUploads';
import { useAuthStore } from 'store/authStore';
import { FileExtIcon, formatSizeKb, formatDamDate } from './fileIcons';

type FolderWithCount = DamFolder & { fileCount: number };

export default function BrowseTab() {
  const [folders, setFolders] = useState<FolderWithCount[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [files, setFiles] = useState<DamFile[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [folderQuery, setFolderQuery] = useState('');

  const user = useAuthStore((s) => s.user);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<DamFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadDepartment, setUploadDepartment] = useState<string>(DAM_DEPARTMENTS[0]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const uploadsAvailable = isIndexedDbAvailable(); // EC-10

  const reloadFolder = (folderId: string) => {
    setFilesLoading(true);
    listDamFolderFiles(folderId).then((items) => {
      setFiles(items);
      setFilesLoading(false);
    });
    // обновить счётчики папок
    listDamFolders().then(setFolders);
  };

  const handleDownload = async (file: DamFile) => {
    setDownloadingId(file.id);
    try {
      await downloadDamFile(file);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Не удалось скачать файл'); // EC-7
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (file: DamFile) => {
    if (!selectedId) return;
    setDeletingId(file.id);
    try {
      if (file.docType === 'Загруженный' || file.id.startsWith('upload-')) {
        await deleteUploadedFile(file.id);
      } else {
        await deleteCreatedFile(file.id);
      }
      reloadFolder(selectedId);
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpload = async (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0 || !selectedId) return;
    setUploadError(null);
    setUploading(true);
    try {
      await uploadDamFile(selectedId, incoming[0], user?.name ?? 'Пользователь', uploadDepartment);
      reloadFolder(selectedId);
    } catch (e) {
      // EC-8/EC-9/EC-10: понятное сообщение, без падений
      setUploadError(e instanceof UploadValidationError ? e.message : 'Не удалось загрузить файл');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    let cancelled = false;
    listDamFolders().then((items) => {
      if (cancelled) return;
      setFolders(items);
      setFoldersLoading(false);
      if (items.length > 0) setSelectedId(items[0].id);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setFilesLoading(true);
    setFolderQuery('');
    listDamFolderFiles(selectedId).then((items) => {
      if (cancelled) return;
      setFiles(items);
      setFilesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const selected = folders.find((folder) => folder.id === selectedId);
  const visibleFiles = folderQuery
    ? files.filter((file) => file.name.toLowerCase().includes(folderQuery.toLowerCase()))
    : files;

  const cardSx = {
    background: '#FFFFFF',
    border: '1px solid var(--color-border)',
    boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 2, alignItems: 'start' }}>
      {/* Дерево папок */}
      <Box sx={cardSx}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--color-border)' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Папки</Typography>
        </Box>
        {foldersLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ px: 2, py: 1 }}>
              <Skeleton height={20} />
            </Box>
          ))}
        {!foldersLoading &&
          folders.map((folder) => {
            const isActive = folder.id === selectedId;
            return (
              <Box
                key={folder.id}
                onClick={() => setSelectedId(folder.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.1,
                  cursor: 'pointer',
                  transition: 'background 0.12s',
                  bgcolor: isActive ? 'rgba(20,116,255,0.07)' : 'transparent',
                  borderLeft: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
                  '&:hover': {
                    bgcolor: isActive ? 'rgba(20,116,255,0.07)' : 'rgba(20,116,255,0.03)',
                  },
                }}
              >
                {isActive ? (
                  <FolderOpen size={15} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                ) : (
                  <Folder size={15} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }} />
                )}
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: isActive ? 650 : 500,
                    color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  }}
                  noWrap
                >
                  {folder.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                  {folder.fileCount}
                </Typography>
              </Box>
            );
          })}
      </Box>

      {/* Файлы папки */}
      <Box sx={cardSx}>
        {/* Breadcrumbs + поиск внутри папки */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>
            Хранилище
          </Typography>
          <ChevronRight size={13} style={{ color: 'var(--color-text-muted)' }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, flex: 1 }} noWrap>
            {selected?.name ?? '—'}
          </Typography>
          <TextField
            size="small"
            placeholder="Поиск в папке..."
            value={folderQuery}
            onChange={(e) => setFolderQuery(e.target.value)}
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root': {
                fontSize: 12.5,
                '& fieldset': { borderColor: 'var(--color-border)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
              },
            }}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', mr: 0.75, color: 'var(--color-text-muted)' }}>
                  <Search size={13} />
                </Box>
              ),
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".pdf,.docx,.xlsx,.pptx,.txt,.md"
            onChange={(e) => void handleUpload(e.target.files)}
          />
          <TextField
            select
            size="small"
            label="Подразделение"
            value={uploadDepartment}
            onChange={(e) => setUploadDepartment(e.target.value)}
            disabled={uploading || !uploadsAvailable}
            sx={{
              minWidth: 180,
              flexShrink: 0,
              '& .MuiOutlinedInput-root': {
                fontSize: 12.5,
                '& fieldset': { borderColor: 'var(--color-border)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
              },
              '& .MuiInputLabel-root.Mui-focused': { color: 'var(--color-primary)' },
            }}
          >
            {DAM_DEPARTMENTS.map((d) => (
              <MenuItem key={d} value={d} sx={{ fontSize: 12.5 }}>
                {d}
              </MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            startIcon={uploading ? <CircularProgress size={13} /> : <Upload size={14} />}
            disabled={uploading || !uploadsAvailable}
            onClick={() => fileInputRef.current?.click()}
            title={
              uploadsAvailable ? undefined : 'Браузер не поддерживает локальное хранилище файлов'
            }
            sx={{
              textTransform: 'none',
              fontSize: 12.5,
              fontWeight: 600,
              flexShrink: 0,
              color: 'var(--color-primary)',
            }}
          >
            Загрузить
          </Button>
        </Box>

        {uploadError && (
          <Typography sx={{ px: 2.5, py: 1, fontSize: 12, color: '#dc2626' }}>
            {uploadError}
          </Typography>
        )}

        <Box
          onDragOver={(e) => {
            e.preventDefault();
            if (uploadsAvailable) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (uploadsAvailable) void handleUpload(e.dataTransfer.files);
          }}
          sx={{
            outline: dragOver ? '2px dashed var(--color-primary)' : 'none',
            outlineOffset: -6,
            borderRadius: 'var(--radius-md)',
            transition: 'outline 0.12s',
          }}
        >
          {filesLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <Box key={i} sx={{ px: 2.5, py: 1.25 }}>
                <Skeleton height={20} />
              </Box>
            ))}

          {!filesLoading && visibleFiles.length === 0 && (
            <Box sx={{ py: 5, textAlign: 'center', color: 'var(--color-text-muted)' }}>
              <Typography sx={{ fontSize: 13 }}>В папке нет файлов по запросу</Typography>
            </Box>
          )}

          {!filesLoading &&
            visibleFiles.map((file) => (
              <Box
                key={file.id}
                onClick={() => setPreviewFile(file)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '24px 1fr 110px 90px 110px 96px',
                  gap: 1.5,
                  px: 2.5,
                  py: 1.2,
                  alignItems: 'center',
                  borderBottom: '1px solid var(--color-border)',
                  cursor: 'pointer',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: 'rgba(20,116,255,0.03)' },
                }}
              >
                <FileExtIcon ext={file.ext} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                    {file.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'var(--color-text-muted)' }} noWrap>
                    {file.author} · {file.department}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  {file.docType}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                  {formatSizeKb(file.sizeKb)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                  {formatDamDate(file.updatedAt)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <IconButton
                    size="small"
                    aria-label={`Просмотреть ${file.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewFile(file);
                    }}
                    sx={{
                      color: 'var(--color-text-muted)',
                      '&:hover': { color: 'var(--color-primary)' },
                    }}
                  >
                    <Eye size={14} />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Скачать ${file.name}`}
                    disabled={downloadingId === file.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDownload(file);
                    }}
                    sx={{
                      color: 'var(--color-text-muted)',
                      '&:hover': { color: 'var(--color-primary)' },
                    }}
                  >
                    {downloadingId === file.id ? (
                      <CircularProgress size={13} />
                    ) : (
                      <Download size={14} />
                    )}
                  </IconButton>
                  {(file.docType === 'Сгенерированный' || file.docType === 'Загруженный') && (
                    <IconButton
                      size="small"
                      aria-label="Удалить файл"
                      disabled={deletingId === file.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleDelete(file);
                      }}
                      sx={{
                        color: 'var(--color-text-muted)',
                        '&:hover': { color: '#dc2626' },
                      }}
                    >
                      {deletingId === file.id ? (
                        <CircularProgress size={13} />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
        </Box>
      </Box>

      <DamPreviewDialog
        open={previewFile !== null}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
}
